import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { uniqBy } from 'lodash';
import { isArray, isEmpty, isEqual, isString, isUndefined } from 'lodash/lang';

import { FinTriggerApi } from '../../../../../../../api';
import ModalBoxes from '../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import { SingleSelectList } from '../../../../../../../common/inputs/SingleSelectList/SingleSelectList';
import { toCoins } from '../../../../../../../services/financial';
import { onRequestError } from '../../../../../../../services/handlers';
import { BUDGET_EVENT_NAME_LENGTH } from '../../budgetConstant';
import { calculateSiteAmount } from '../../services';
import BudgetEventControlButtons from '../BudgetEventControlButtons';
import { DIRECT_COST, PASS_THRU } from '../ExpenseType';
import { UpdatedBudgetEventForm } from '../UpdatedBudgetEventForm';

import './AddOrEditEncounterItemGroupFieldBudgetEvent.scss';

const maxCount = Number.MAX_SAFE_INTEGER;

const AddOrEditEncounterItemGroupFieldBudgetEvent = ({
  data,
  budgetEventType,
  encounterTriggers,
  itemGroupTriggers,
  vendors,
  triggerType,
  managementFeePercent,
  onSave,
  modalBox,
  currentStudyBudget,
  studyId
}) => {
  const {
    groupId,
    triggerId,
    triggerName,
    name,
    overhead,
    withholding,
    managementFee,
    vendorId,
    eventRows,
    nonInvoiceable,
    excludedFromRevenueProjection
  } = data || {};

  const initialVendorId = useMemo(() => vendorId || 'NOT_SELECTED', [vendorId]);

  const emptyRow = useMemo(
    () => ({
      id: null,
      countFrom: 1,
      countTo: maxCount,
      clientAmount: 0,
      siteAmount: 0,
      siteAmountType: PASS_THRU,
      vendorAmount: 0,
      vendorAmountType: null
    }),
    []
  );

  const initialState = useMemo(() => {
    const isEdit = !isUndefined(data);
    return {
      isEdit: isEdit,
      selectedTrigger: null,
      selectedItemGroup: null,
      selectedFieldTrigger: isEdit ? { id: triggerId, type: triggerType } : null,
      groupId: groupId,
      name: name || '',
      overhead: !!overhead,
      withholding: !!withholding,
      managementFee: isUndefined(managementFee) ? +managementFeePercent !== 0 : managementFee,
      selectedVendorId: initialVendorId,
      eventRows: isArray(eventRows) ? eventRows : [emptyRow],
      showOverlapsErrorMsg: isArray(eventRows) ? new Array(eventRows.length).fill(false) : [false],
      nonInvoiceable: !!nonInvoiceable,
      excludedFromRevenueProjection: isEdit ? !!excludedFromRevenueProjection : true
    };
  }, [
    data,
    emptyRow,
    eventRows,
    groupId,
    initialVendorId,
    managementFee,
    managementFeePercent,
    name,
    nonInvoiceable,
    overhead,
    triggerId,
    triggerType,
    withholding,
    excludedFromRevenueProjection
  ]);

  const [modalState, setModalState] = useState({
    ...initialState,
    TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD: [],
    TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD: [],
    TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD: []
  });

  const fieldTriggers = useMemo(() => {
    return [
      ...modalState.TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD,
      ...modalState.TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD,
      ...modalState.TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD
    ];
  }, [
    modalState.TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD,
    modalState.TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD,
    modalState.TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD
  ]);

  const visibleEncounters = useMemo(() => {
    if (isEmpty(fieldTriggers)) {
      return [];
    }
    const fieldTriggerEncounterIds = fieldTriggers?.map(f => f.encounterId);
    return encounterTriggers?.filter(f => fieldTriggerEncounterIds?.includes(f.encounterId));
  }, [encounterTriggers, fieldTriggers]);

  const visibleItemGroupsTriggers = useMemo(() => {
    if (isEmpty(fieldTriggers)) {
      return [];
    }
    if (isEmpty(modalState.selectedTrigger)) {
      return itemGroupTriggers;
    }

    let visibleItemGroupTriggers;
    let triggers = [];
    switch (modalState.selectedTrigger?.type) {
      case 'UNEXPECTED_ENCOUNTER':
        const unexpectedItemGroupIds = modalState.TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD?.map(f => f.itemGroupKey);
        visibleItemGroupTriggers = itemGroupTriggers.filter(f => unexpectedItemGroupIds?.includes(f.itemGroupKey));
        break;
      case 'SITUATIONAL_ENCOUNTER':
        const situationalItemGroupIds = modalState.TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD?.map(f => f.itemGroupKey);
        visibleItemGroupTriggers = itemGroupTriggers.filter(f => situationalItemGroupIds?.includes(f.itemGroupKey));
        break;
      default:
        triggers = modalState.TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD.filter(
          f => f.encounterId === modalState.selectedTrigger?.encounterId
        );
        triggers = triggers.map(t => t.itemGroupKey);
        visibleItemGroupTriggers = itemGroupTriggers.filter(f => triggers.includes(f.itemGroupKey));
    }
    return visibleItemGroupTriggers;
  }, [
    fieldTriggers,
    itemGroupTriggers,
    modalState.TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD,
    modalState.TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD,
    modalState.TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD,
    modalState.selectedTrigger
  ]);

  const visibleFieldTriggers = useMemo(() => {
    if (isEmpty(modalState.selectedTrigger) && isEmpty(modalState.selectedItemGroup)) {
      return uniqBy(fieldTriggers, 'name');
    }

    const filterBySelection = arr =>
      arr.filter(f => {
        const matchesEncounter = f.encounterId === modalState.selectedTrigger?.encounterId;
        return isEmpty(modalState.selectedItemGroup)
          ? matchesEncounter
          : matchesEncounter && f.itemGroupKey === modalState.selectedItemGroup?.itemGroupKey;
      });

    switch (modalState.selectedTrigger?.type) {
      case 'UNEXPECTED_ENCOUNTER':
        return isEmpty(modalState.selectedItemGroup)
          ? modalState.TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD
          : modalState.TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD.filter(
              el => el.itemGroupKey === modalState.selectedItemGroup?.itemGroupKey
            );
      case 'SITUATIONAL_ENCOUNTER':
        return filterBySelection(modalState.TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD);
      default:
        return filterBySelection(modalState.TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD);
    }
  }, [
    fieldTriggers,
    modalState.TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD,
    modalState.TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD,
    modalState.TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD,
    modalState.selectedItemGroup,
    modalState.selectedTrigger
  ]);

  useEffect(() => {
    Promise.all([
      FinTriggerApi.getUnexpectedItemGroupFieldsTriggers(studyId),
      FinTriggerApi.getSituationalItemGroupFieldsTriggers(studyId),
      FinTriggerApi.getEncounterItemGroupFieldsTriggers(studyId)
    ]).then(([unexpectedRes, situationalRes, encounterRes]) => {
      const normalizedUnexpected = unexpectedRes.data
        ? unexpectedRes.data.map(trigger => ({
            ...trigger,
            encounterId: 'UnexpectedEncounter'
          }))
        : [];

      setModalState(prevState => ({
        ...prevState,
        TRIGGERS_UNEXPECTED_ITEM_GROUP_FIELD: normalizedUnexpected,
        TRIGGERS_ENCOUNTER_ITEM_GROUP_FIELD: encounterRes.data,
        TRIGGERS_SITUATIONAL_ITEM_GROUP_FIELD: situationalRes.data
      }));
    }, onRequestError);
  }, [studyId]);

  useEffect(() => {
    if (modalState.managementFee) {
      setModalState(prevState => {
        const updatedEventRows = prevState.eventRows.map(budgetEvent => ({
          ...budgetEvent,
          siteAmount: calculateSiteAmount(budgetEvent.clientAmount, managementFeePercent, true)
        }));
        return {
          ...prevState,
          eventRows: !isEqual(updatedEventRows, prevState.eventRows) ? updatedEventRows : prevState.eventRows
        };
      });
    }
  }, [modalState.managementFee, managementFeePercent, modalState.eventRows]);

  const onSelectEncounters = useCallback(enc => {
    setModalState(prevState => {
      return {
        ...prevState,
        selectedTrigger: enc,
        selectedItemGroup: null,
        selectedFieldTrigger: null
      };
    });
  }, []);

  const onSelectItemGroups = useCallback(option => {
    setModalState(prevState => {
      if (isEqual(prevState.selectedItemGroup, option)) {
        return {
          ...prevState,
          selectedItemGroup: null,
          selectedFieldTrigger: null
        };
      }
      return {
        ...prevState,
        selectedItemGroup: option,
        selectedFieldTrigger: null
      };
    });
  }, []);

  const revertOrSetUpVendorRadioButtons = useCallback(() => {
    setModalState(prevState => {
      const { eventRows, selectedVendorId } = prevState;
      if (selectedVendorId !== 'NOT_SELECTED' && eventRows.every(e => !e.vendorAmountType)) {
        const updatedEventRows = eventRows.map(e => ({ ...e, vendorAmountType: DIRECT_COST }));
        return { ...prevState, eventRows: updatedEventRows };
      } else if (selectedVendorId === 'NOT_SELECTED' && eventRows.some(e => !!e.vendorAmountType)) {
        const updatedEventRows = eventRows.map(e => ({ ...e, vendorAmountType: null }));
        return { ...prevState, eventRows: updatedEventRows };
      }
      return prevState;
    });
  }, []);

  const onChangeFormControls = useCallback(
    ({ target }) => {
      if (target.type === 'checkbox') {
        if (target.name === 'managementFee') {
          setModalState(prevState => ({
            ...prevState,
            managementFee: !prevState.managementFee,
            eventRows: prevState.managementFee
              ? prevState.eventRows.map(budgetEvent => ({
                  ...budgetEvent,
                  siteAmount: 0
                }))
              : prevState.eventRows
          }));
        } else {
          setModalState(prevState => ({ ...prevState, [target.name]: target.checked }));
        }
      } else {
        setModalState({
          ...modalState,
          [target.name]: target.value
        });
        revertOrSetUpVendorRadioButtons();
      }
    },
    [modalState, revertOrSetUpVendorRadioButtons]
  );

  const checkOverlappedFields = useCallback(idx => {
    setModalState(prevState => {
      const currentRow = prevState.eventRows[idx];
      let isRowValid = true;
      if (currentRow) {
        const isFromEmpty = isUndefined(currentRow.countFrom);
        const isFromPositive = currentRow.countFrom > 0;
        const isFromLessOrEqualsTo = currentRow.countFrom <= currentRow.countTo;
        const rangesDoNotOverlap = prevState.eventRows
          .filter((range, i) => i !== idx)
          .every(range => range.countTo < currentRow.countFrom || range.countFrom > currentRow.countTo);
        isRowValid = !isFromEmpty && isFromPositive && isFromLessOrEqualsTo && rangesDoNotOverlap;
      }
      return {
        ...prevState,
        showOverlapsErrorMsg: prevState.showOverlapsErrorMsg.map((el, index) => (index === idx ? !isRowValid : el))
      };
    });
  }, []);

  const onChangeEventRow = useCallback(
    idx => evt => {
      const targetBaseName = evt.target.name.split('_')[0];
      setModalState(prevState => {
        const newEventRows = prevState.eventRows.map((eventRow, index) => {
          if (index === idx) {
            return {
              ...eventRow,
              [targetBaseName]:
                targetBaseName === 'countTo' && isUndefined(evt.target.value) ? maxCount : evt.target.value
            };
          }
          return eventRow;
        });
        return { ...prevState, eventRows: newEventRows };
      });
      if (targetBaseName === 'countFrom' || targetBaseName === 'countTo') {
        modalState.showOverlapsErrorMsg.forEach((elem, index) => checkOverlappedFields(index));
      }
    },
    [checkOverlappedFields, modalState.showOverlapsErrorMsg]
  );

  const handleAddEventRow = useCallback(() => {
    const newRows = modalState.eventRows.concat([
      modalState.selectedVendorId === 'NOT_SELECTED'
        ? emptyRow
        : {
            ...emptyRow,
            vendorAmountType: DIRECT_COST
          }
    ]);
    setModalState(prevState => ({
      ...prevState,
      eventRows: newRows,
      showOverlapsErrorMsg: prevState.showOverlapsErrorMsg.concat([false])
    }));
    newRows.forEach((elem, index) => checkOverlappedFields(index));
  }, [checkOverlappedFields, emptyRow, modalState.eventRows, modalState.selectedVendorId]);

  const handleRemoveEventRow = useCallback(
    idx => () => {
      setModalState(prevState => ({
        ...prevState,
        eventRows: prevState.eventRows.filter((row, rowIdx) => idx !== rowIdx),
        showOverlapsErrorMsg: prevState.showOverlapsErrorMsg.filter((item, itemIdx) => idx !== itemIdx)
      }));
      modalState.showOverlapsErrorMsg.forEach((elem, index) => checkOverlappedFields(index));
    },
    [checkOverlappedFields, modalState.showOverlapsErrorMsg]
  );

  const save = useCallback(() => {
    const {
      name,
      overhead,
      withholding,
      managementFee,
      selectedVendorId,
      eventRows,
      selectedFieldTrigger,
      excludedFromRevenueProjection
    } = modalState;
    const data = eventRows.map(row => {
      return {
        id: row.id,
        type: selectedFieldTrigger?.type,
        name: name,
        overhead: overhead,
        withholding: withholding,
        managementFee: managementFee,
        nonInvoiceable: modalState.nonInvoiceable,
        finVendorId: selectedVendorId === 'NOT_SELECTED' ? null : selectedVendorId,
        countFrom: row.countFrom,
        countTo: row.countTo === maxCount ? null : row.countTo,
        clientAmount: toCoins(row.clientAmount),
        siteAmount: toCoins(row.siteAmount),
        siteAmountType: row.siteAmountType,
        vendorAmount: selectedVendorId !== 'NOT_SELECTED' ? toCoins(row.vendorAmount) : 0,
        vendorAmountType: row.vendorAmountType,
        finTriggerId: selectedFieldTrigger?.id || null,
        excludedFromRevenueProjection
      };
    });
    return onSave(data, modalState.groupId);
  }, [modalState, onSave]);

  const onSaveButton = useCallback(() => {
    save();
    modalBox.close();
  }, [modalBox, save]);

  const onSaveAndContinue = useCallback(() => {
    save().then(() =>
      setModalState(prevState => ({
        ...prevState,
        ...initialState
      }))
    );
  }, [initialState, save]);

  const isValidForm = useCallback(() => {
    const {
      isEdit,
      selectedTrigger,
      name,
      selectedVendorId,
      showOverlapsErrorMsg,
      eventRows,
      selectedFieldTrigger,
      selectedItemGroup
    } = modalState;

    return (
      ((!isEmpty(selectedTrigger) && !isEmpty(selectedItemGroup) && !isEmpty(selectedFieldTrigger)) || isEdit) &&
      isString(name) &&
      name.trim() !== '' &&
      showOverlapsErrorMsg.every(isError => !isError) &&
      eventRows.every(
        row =>
          row.clientAmount >= 0 &&
          (!row.siteAmount || row.siteAmount >= 0) &&
          (selectedVendorId === 'NOT_SELECTED' || row.vendorAmount > 0)
      ) &&
      name.length <= BUDGET_EVENT_NAME_LENGTH
    );
  }, [modalState]);

  const isEditPossible = useCallback(() => modalState.isEdit, [modalState.isEdit]);

  const onChangeFieldOption = useCallback(option => {
    setModalState(prevState => {
      if (isEqual(prevState.selectedFieldTrigger, option)) {
        return {
          ...prevState,
          selectedFieldTrigger: null
        };
      }
      return { ...prevState, selectedFieldTrigger: option };
    });
  }, []);

  return (
    <>
      <ModalBoxes.Body>
        {!isEditPossible() && isArray(visibleEncounters) && (
          <Box
            sx={{
              display: 'flex',
              width: '750px'
            }}
          >
            <Box
              sx={{
                overflow: 'auto'
              }}
            >
              <SingleSelectList
                options={visibleEncounters}
                label="name"
                selectedOption={modalState.selectedTrigger}
                onSelect={onSelectEncounters}
              />
            </Box>
            <Box
              sx={{
                overflow: 'auto'
              }}
            >
              <SingleSelectList
                options={visibleItemGroupsTriggers}
                label="name"
                selectedOption={modalState.selectedItemGroup}
                onSelect={onSelectItemGroups}
              />
            </Box>
            <Box
              sx={{
                overflow: 'auto'
              }}
            >
              <SingleSelectList
                options={visibleFieldTriggers}
                label="name"
                selectedOption={modalState.selectedFieldTrigger}
                onSelect={onChangeFieldOption}
              />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            padding: '0 15px',
            overflow: 'auto',
            flexDirection: 'column',
            width: '670px'
          }}
        >
          <UpdatedBudgetEventForm
            isEventRanges={true}
            isEdit={isEditPossible}
            triggerName={triggerName}
            name={modalState.name}
            overhead={modalState.overhead}
            withholding={modalState.withholding}
            managementFee={modalState.managementFee}
            managementFeePercent={managementFeePercent}
            finVendors={vendors}
            selectedVendorId={modalState.selectedVendorId}
            budgetEventType={budgetEventType}
            currentStudyBudget={currentStudyBudget}
            eventRows={modalState.eventRows}
            maxCount={maxCount}
            showOverlapsErrorMsg={modalState.showOverlapsErrorMsg}
            onChangeFormControls={onChangeFormControls}
            onChangeEventRow={onChangeEventRow}
            handleAddEventRow={handleAddEventRow}
            handleRemoveEventRow={handleRemoveEventRow}
            nonInvoiceable={modalState.nonInvoiceable}
            excludedFromRevenueProjection={modalState.excludedFromRevenueProjection}
          />
        </Box>
      </ModalBoxes.Body>
      <BudgetEventControlButtons
        isValidForm={isValidForm}
        onClose={modalBox.close}
        onSave={onSaveButton}
        onSaveAndContinue={isEmpty(data) ? onSaveAndContinue : null}
        currentStudyBudget={currentStudyBudget}
      />
    </>
  );
};

AddOrEditEncounterItemGroupFieldBudgetEvent.title = 'Add Encounter Item Group Field budget event';
AddOrEditEncounterItemGroupFieldBudgetEvent.className = 'add-encounter-item-group-field-modal';

export default AddOrEditEncounterItemGroupFieldBudgetEvent;
